/**
 * Widget paneli: serverdan o'qish va saqlash. Har o'zgarish darhol ko'rinadi (optimistic), serverga navbat bilan
 * ketadi: bitta so'rov uchib turganda keyingi o'zgarishlar yig'iladi va oxirgisi yangi versiya bilan yuboriladi
 * (o'zimizga 409 bermaymiz). Boshqa oyna/qurilma o'zgartirsa -- 409: server holati olinadi va xabar chiqadi.
 */
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import i18n from "i18next";
import { api, ApiError } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";
import { subscribeEvents } from "@/lib/realtime";
import { toast } from "@/components/ui/toast";
import type { Layout } from "./types";

const URL = "/api/widgets/layout/";
// Real-time hodisasi "widget_layout" -- kalit boshqacha: umumiy invalidatsiya emas, versiyaga qarab (pastda)
export const widgetKeys = { layout: ["widgets", "layout"] as const };

type Content = Pick<Layout, "items" | "glance">;

let pending: Content | null = null;
let inFlight = false;

const cached = () => queryClient.getQueryData<Layout>(widgetKeys.layout);

/** Ekrandagi (saqlanayotgan bo'lsa ham) tarkib: kechiktirilgan amallar (menyu, "Qaytarish") eskisini olmasin */
export const currentLayout = cached;

function isLayout(value: unknown): value is Layout {
  const v = value as Layout | null;
  return !!v && Array.isArray(v.items) && Array.isArray(v.glance) && typeof v.version === "number";
}

export function saveLayout(next: Content) {
  const current = cached();
  if (!current) return;
  queryClient.setQueryData<Layout>(widgetKeys.layout, { ...current, ...next });
  pending = next;
  if (!inFlight) void flush();
}

async function flush() {
  inFlight = true;
  try {
    while (pending) {
      const body = pending;
      pending = null;
      try {
        const saved = await api.put<Layout>(URL, { ...body, version: cached()?.version ?? 0 });
        // Navbatda yana o'zgarish bo'lsa -- ekrandagisi qoladi, faqat versiya yangilanadi
        queryClient.setQueryData<Layout>(widgetKeys.layout, (old) => (pending && old ? { ...old, version: saved.version } : saved));
      } catch (err) {
        pending = null;
        if (err instanceof ApiError && err.isConflict && isLayout(err.current)) {
          queryClient.setQueryData<Layout>(widgetKeys.layout, err.current);
          toast.warning(i18n.t("widgets:conflict"));
        } else {
          toast.error(err instanceof ApiError ? err.message : i18n.t("widgets:saveFailed"));
          void queryClient.invalidateQueries({ queryKey: widgetKeys.layout });
        }
      }
    }
  } finally {
    inFlight = false;
  }
}

export function useWidgetLayout(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    // O'zimizning saqlashimiz ham hodisa yuboradi: faqat yangiroq versiya kelganda va navbat bo'sh bo'lsa qayta o'qiymiz
    const off = subscribeEvents((ev) => {
      if (ev.entity !== "widget_layout" || inFlight || pending) return;
      if ((ev.version ?? 0) > (cached()?.version ?? 0)) void queryClient.invalidateQueries({ queryKey: widgetKeys.layout });
    });
    return () => {
      off();
    };
  }, [enabled]);
  return useQuery({
    queryKey: widgetKeys.layout,
    queryFn: () => api.get<Layout>(URL),
    enabled,
    staleTime: 5 * 60_000,
    // Saqlash navbatida o'zgarish turganda fokusdagi qayta o'qish ekrandagini eskisi bilan almashtirmasin
    refetchOnWindowFocus: () => !inFlight && !pending,
  });
}
